import { Directive, HostBinding, HostListener, inject, Input } from '@angular/core';
import { Router } from '@angular/router';
import { RouteUtil } from '../util/route-util';

/**
 * Drop-in replacement for plain `href` or `[routerLink]` on anchor elements.
 * Automatically decides whether to use Angular SPA navigation or a regular
 * browser href based on the URL:
 *
 *   - Internal paths (/ux/cart/..., /ux/dotcom/...): intercepts the click and
 *     navigates via the Angular router (no full page reload). For internal
 *     URLs we deliberately ignore `target="_blank"` so cart/dotcom links
 *     always stay in the same tab — content authors and translation values
 *     occasionally set `_blank` on shop links and we don't want that to
 *     spawn a second SPA instance.
 *   - External / hash URLs: falls through to normal browser navigation,
 *     including respecting `target="_blank"`.
 *
 * Modifier-key clicks (ctrl/cmd/shift) and middle-clicks still open in a new
 * tab naturally for both internal and external URLs.
 *
 * The `href` attribute is always set so right-click and keyboard access work
 * correctly regardless of whether the URL is internal or external.
 *
 * Usage:
 *   <a [appLink]="url" [target]="openInNew ? '_blank' : '_self'">Label</a>
 */
@Directive({
    selector: 'a[appLink]',
    standalone: true,
})
export class AppLinkDirective {
    @Input({ required: true }) appLink: string | null | undefined;

    private readonly router = inject(Router);

    @HostBinding('attr.href')
    get href(): string | null {
        return this.appLink ?? null;
    }

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        const url = this.appLink;
        if (!RouteUtil.isInternalUrl(url)) {
            return;
        }
        // Let modifier-key clicks and middle-clicks open a new tab naturally.
        if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey) {
            return;
        }
        event.preventDefault();
        this.router.navigateByUrl(url);
    }
}
